// Sunbathers, swimmers, waders and the children at the sandcastle.
import * as THREE from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { PI } from '../../lib/math.js';
import { rnd, rr } from '../../lib/random.js';
import { scene } from '../../core/scene.js';
import { sandH } from '../../world/shore.js';
import { V } from '../../world/sagrada.js';
import { beachLie, swimSpots, waders } from '../crowd.js';
import { WALK_SPEED } from '../walking.js';
import { bobbers, idlers, wading } from './loader.js';
import { makeClip, swimFrame } from './animation.js';
import { prepared } from './prepared.js';
import { dress, swimOutfit } from './outfits.js';
import { HQ, X, Y, Z, rotW, seat } from './seating.js';
import { addGlasses, lyingActivity } from './activities.js';

export let beachModel;

export function buildBeachPeople() {
  // ---------- beach people: sunbathers on towels, swimmers, waders, a volleyball game ----------
  const MAN = prepared[0],
    WOMAN = prepared[5];
  beachModel = (holder, fem) => {
    const P = fem ? WOMAN : MAN,
      model = dress(SkeletonUtils.clone(P.root), P, swimOutfit(fem));
    model.scale.setScalar(P.k * rr(0.94, 1.05));
    holder.add(model);
    model.traverse((o) => {
      if (o.isSkinnedMesh) {
        o.castShadow = true;
        o.frustumCulled = false;
      }
    });
    holder.updateMatrixWorld(true);
    holder.getWorldQuaternion(HQ);
    return {
      model,
      P,
      b: (n) => model.getObjectByName(THREE.PropertyBinding.sanitizeNodeName(n)),
    };
  };
  // put the model's hips at holder-local (x, y, z) after posing and turning it
  const placeHips = (holder, model, b, y) => {
    holder.updateMatrixWorld(true);
    const h = holder.worldToLocal(b('Hips').getWorldPosition(new THREE.Vector3()));
    model.position.x -= h.x;
    model.position.z -= h.z;
    model.position.y += y - h.y;
  };
  beachLie.forEach((t, i) => {
    const holder = new THREE.Group();
    holder.position.set(t.x, t.y, t.z);
    holder.rotation.y = t.r + (i % 2 ? PI : 0);
    scene.add(holder);
    t.holder = holder;
    const fem = rnd() < 0.5,
      down = rnd() < 0.35,
      { model, b } = beachModel(holder, fem);
    const act = down
      ? rnd() < 0.45
        ? 'bookOnTowel'
        : ''
      : ((r2) => (r2 < 0.3 ? 'bookUp' : r2 < 0.55 ? 'phoneUp' : ''))(rnd());
    for (const s of ['L', 'R']) {
      const sg = s === 'L' ? 1 : -1;
      // arms along the body, a little out from the sides (face down too: reaching forward looked broken)
      rotW(b('UpperArm.' + s), Z, -sg * rr(1.25, 1.42));
      rotW(b('UpperArm.' + s), Y, sg * 0.1);
      if (down) rotW(b('LowerArm.' + s), Z, -sg * 0.15); // forearms resting flat on the towel
      rotW(b('UpperLeg.' + s), Y, sg * rr(0.03, 0.1));
    }
    if (!down && rnd() < 0.4) {
      rotW(b('UpperLeg.L'), X, -0.75);
      rotW(b('LowerLeg.L'), X, 1.45);
    } // one knee up
    // sunglasses go on while the model is still upright and facing ahead; the head then carries them as it turns
    if (!down && rnd() < 0.6) addGlasses(model, b);
    rotW(b('Head'), Y, rr(-0.6, 0.6));
    // lie back (front to the sky) or face down; the body runs along the towel
    model.rotation.x = down ? PI / 2 : -PI / 2;
    placeHips(holder, model, b, 0.075);
    if (act) lyingActivity(holder, model, b, act);
    holder.updateMatrixWorld(true);
    holder.matrixWorldAutoUpdate = false;
  });
  swimSpots.forEach((sp) => {
    const holder = new THREE.Group();
    holder.position.set(sp.x, -0.33, sp.z);
    holder.rotation.y = sp.r;
    scene.add(holder);
    const fem = rnd() < 0.5,
      { model, b, P } = beachModel(holder, fem);
    // swimming breaststroke, each at its own pace; the neck is set just above the surface from the first frame
    P.swim ??= makeClip('Swim', P.root, null, 1.7, swimFrame);
    const mixer = new THREE.AnimationMixer(model),
      a = mixer.clipAction(P.swim);
    a.play();
    a.time = rr(0, 1.7);
    a.timeScale = rr(0.85, 1.15);
    mixer.update(0);
    idlers.push(mixer);
    Object.assign(sp, { holder, mixer });
    holder.updateMatrixWorld(true);
    const nk = holder.worldToLocal(b('Neck').getWorldPosition(new THREE.Vector3()));
    model.position.y += 0.035 - nk.y;
    // each swimmer laps a small circle at breaststroke pace (~0.35 u/s), some clockwise
    const rad = rr(1.3, 2.4),
      dir = rnd() < 0.5 ? 1 : -1;
    // the loop stays in the water (shoreline at z ≈ 63) and inside the buoy line (z ≈ 74)
    bobbers.push(
      (sp.bob = {
        g: holder,
        y: -0.33,
        r: sp.r,
        ph: rr(0, 6),
        cx: sp.x - Math.cos(sp.r) * rad,
        cz: THREE.MathUtils.clamp(sp.z - Math.sin(sp.r) * rad, 66 + rad, 73.5 - rad),
        rad,
        ang: sp.r,
        w: (dir * 0.35) / rad,
      }),
    );
  });
  // two kids sitting in the sand by the castle, busy with it
  for (const [x, z, r] of [
    [window.CASTLE.x - 0.55, window.CASTLE.z + 0.05, PI / 2],
    [window.CASTLE.x + 0.35, window.CASTLE.z + 0.55, PI + 0.6],
  ]) {
    const holder = new THREE.Group();
    holder.position.set(x, 0, z);
    holder.rotation.y = r;
    scene.add(holder);
    const fem = rnd() < 0.5,
      P = fem ? WOMAN : MAN;
    seat(P, holder, P.k * 0.62, V(0, sandH(x, z) + 0.04, 0), 'ground', null, swimOutfit(fem));
    holder.updateMatrixWorld(true);
    holder.matrixWorldAutoUpdate = false;
  }
  for (const sp of waders) {
    const holder = new THREE.Group();
    holder.position.set(sp.x, sp.y, sp.z);
    holder.rotation.y = sp.r;
    scene.add(holder);
    const { model, P } = beachModel(holder, rnd() < 0.5);
    model.position.y = P.y0 * model.scale.y;
    // a slow wade, legs pushing through the water: the walk clip at the matching (slower) cadence
    const v = rr(0.22, 0.32),
      mixer = new THREE.AnimationMixer(model),
      a = mixer.clipAction(P.walk);
    a.play();
    a.time = rnd();
    a.timeScale = v / WALK_SPEED;
    idlers.push(mixer);
    Object.assign(sp, { holder, mixer, v });
    wading.push({
      holder,
      v,
      dir: rnd() < 0.5 ? 1 : -1,
      x0: sp.x - rr(2, 4),
      x1: sp.x + rr(2, 4),
    });
  }
}
